import { useState } from 'react';
import { Search, X, UserPlus } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import clsx from 'clsx';

export const PlayerSelector = ({ players, onSelect, onCreatePlayer, onClose }) => {
    const { t } = useLanguage();
    const [searchQuery, setSearchQuery] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [newPlayerName, setNewPlayerName] = useState('');

    const filteredPlayers = players.filter(player =>
        player.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleCreate = () => {
        if (newPlayerName.trim()) {
            onCreatePlayer?.(newPlayerName.trim());
            setNewPlayerName('');
            setIsCreating(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="card-premium w-full max-w-md max-h-[80vh] flex flex-col bg-gray-900">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-700">
                    <h2 className="text-xl font-bold">{t('selectPlayer')}</h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Search Bar */}
                {!isCreating && (
                    <div className="p-4 border-b border-gray-700">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder={t('searchPlayers')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-poker-gold text-white placeholder-gray-400"
                            />
                        </div>
                    </div>
                )}

                {/* Player List or Create Form */}
                <div className="flex-1 overflow-y-auto p-4">
                    {isCreating ? (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    {t('playerName')}
                                </label>
                                <input
                                    type="text"
                                    value={newPlayerName}
                                    onChange={(e) => setNewPlayerName(e.target.value)}
                                    placeholder={t('playerName')}
                                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-poker-gold text-white"
                                    autoFocus
                                />
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleCreate}
                                    disabled={!newPlayerName.trim()}
                                    className={clsx(
                                        'flex-1 px-4 py-2 rounded-lg font-medium transition-colors',
                                        newPlayerName.trim()
                                            ? 'bg-poker-gold text-gray-900 hover:bg-poker-gold/90'
                                            : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                                    )}
                                >
                                    {t('add')}
                                </button>
                                <button
                                    onClick={() => {
                                        setIsCreating(false);
                                        setNewPlayerName('');
                                    }}
                                    className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
                                >
                                    {t('cancel')}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Create New Player Button */}
                            <button
                                onClick={() => setIsCreating(true)}
                                className="w-full p-4 mb-4 border-2 border-dashed border-gray-600 hover:border-poker-gold rounded-lg transition-colors flex items-center justify-center gap-2 text-gray-400 hover:text-poker-gold"
                            >
                                <UserPlus className="w-5 h-5" />
                                <span className="font-medium">{t('createNewPlayer')}</span>
                            </button>

                            {/* Existing Players */}
                            {filteredPlayers.length === 0 ? (
                                <div className="text-center text-gray-400 py-8">
                                    {searchQuery ? 'No players found' : 'No players yet'}
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {filteredPlayers.map((player) => (
                                        <button
                                            key={player.id}
                                            onClick={() => onSelect(player)}
                                            className="w-full p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors text-left group"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <div className="font-semibold text-white group-hover:text-poker-gold transition-colors">
                                                        {player.name}
                                                    </div>
                                                    <div className="text-sm text-gray-400 mt-1">
                                                        {t('totalSpent')}: ₪{player.totalSpent || 0} | {' '}
                                                        {t('totalProfitLoss')}: {' '}
                                                        <span className={player.totalProfitLoss >= 0 ? 'text-green-400' : 'text-red-400'}>
                                                            {player.totalProfitLoss >= 0 ? '+' : ''}₪{player.totalProfitLoss || 0}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
